import {
  EDITOR_FIELD_FINAL_RESTING,
  EDITOR_FIELD_NAME_OF_DECEASED,
  EDITOR_FIELD_SERVICE_COLUMN,
  editorFieldCatalogEntry,
} from '@messenger/lotus'
import type { EditorFieldParams } from '@messenger/lotus'
import { FH_FIELD_SOURCES, FIELD_SOURCES } from '../fieldRules'
import { SERVICE_SCALAR_COLUMNS } from '../toEditorTextBoxCaseData'

/**
 * What a TinyMCE field becomes: a field run of a catalog field (bound to a
 * service row, a numbered cell or a service column through its params); a
 * table, when a tabular field stands as a block; or its stored text, frozen,
 * when the catalog has no home for its key or a tabular field stands inline
 * or as a template. A frozen field's text is kept where it shows; where
 * TinyMCE hid it, it converts to nothing and is still reported.
 */
export type TinyMceFieldTarget =
  | {
      kind: 'field'
      fieldKey: string
      caseServiceId?: string
      params?: EditorFieldParams
    }
  | { kind: 'tabular'; fieldKey: string }
  | { kind: 'frozen'; key: string }

const FIRST_LETTER_KEY = 'first_letter_'
const YEAR_KEY = 'year_of_'

const firstLetterSources = FIELD_SOURCES.filter((s) =>
  s.key.startsWith(FIRST_LETTER_KEY),
)
const yearSources = FIELD_SOURCES.filter((s) => s.key.startsWith(YEAR_KEY))

/**
 * TinyMCE's `data-case-detail` spelling of each field a case column feeds,
 * the inverse of the case-data sources: a field is keyed by its column,
 * except that TinyMCE spells a first-letter field `first_letter_<column>`,
 * keys a year only by template, and nests the funeral home's columns under
 * `funeralHomeDetails.`.
 */
const CASE_DETAIL_FIELD_KEYS: ReadonlyMap<string, string> = new Map([
  ...FIELD_SOURCES.filter((s) => !s.key.startsWith(YEAR_KEY)).map(
    (s): [string, string] => [
      s.key.startsWith(FIRST_LETTER_KEY)
        ? `${FIRST_LETTER_KEY}${s.from}`
        : s.from,
      s.key,
    ],
  ),
  ...FH_FIELD_SOURCES.map((s): [string, string] => [
    `funeralHomeDetails.${s.from}`,
    s.key,
  ]),
  ['name_of_deceased', EDITOR_FIELD_NAME_OF_DECEASED],
])

/** The `data-template` values that name a field other than themselves. */
const TEMPLATE_FIELD_KEYS: ReadonlyMap<string, string> = new Map([
  ['made_in', 'printed_in'],
  ['final_resting', EDITOR_FIELD_FINAL_RESTING],
  ...yearSources.map((s): [string, string] => [`${s.from}_year`, s.key]),
  ...firstLetterSources.map((s): [string, string] => [
    `${s.from}_first_letter`,
    s.key,
  ]),
])

const PUBLISHED_SERVICE_COLUMNS: ReadonlySet<string> = new Set(
  SERVICE_SCALAR_COLUMNS,
)

const SERVICE_COLUMN_PATH = /^services\[(\d+)\]\.([a-z_]+)$/
const DEFAULT_SERVICE_COLUMN_PATH = /^default_service\.([a-z_]+)$/
const INDEXED_KEY = /^([a-z_]+)\[(\d+)\]$/

const serviceColumnTarget = (
  column: string,
  serviceIndex?: number,
): TinyMceFieldTarget | undefined =>
  PUBLISHED_SERVICE_COLUMNS.has(column)
    ? {
        kind: 'field',
        fieldKey: EDITOR_FIELD_SERVICE_COLUMN,
        params: {
          serviceColumn: column,
          ...(serviceIndex === undefined ? {} : { serviceIndex }),
        },
      }
    : undefined

/**
 * The indexed paths TinyMCE resolves against case rows: a service's column
 * (`services[0].location`), the default service's (`default_service.time`),
 * and a numbered cell of a tabular field (`clergy[0]`).
 */
const indexedTarget = (key: string): TinyMceFieldTarget | undefined => {
  const service = SERVICE_COLUMN_PATH.exec(key)
  if (service) return serviceColumnTarget(service[2], Number(service[1]))
  const defaultService = DEFAULT_SERVICE_COLUMN_PATH.exec(key)
  if (defaultService) return serviceColumnTarget(defaultService[1])
  const indexed = INDEXED_KEY.exec(key)
  if (indexed && editorFieldCatalogEntry(indexed[1])?.kind === 'tabular')
    return {
      kind: 'field',
      fieldKey: indexed[1],
      params: { cellIndex: Number(indexed[2]) },
    }
  return undefined
}

const catalogTarget = (
  fieldKey: string,
  storedKey: string,
): TinyMceFieldTarget => {
  const kind = editorFieldCatalogEntry(fieldKey)?.kind
  if (kind === undefined) return { kind: 'frozen', key: storedKey }
  if (kind === 'tabular') return { kind: 'tabular', fieldKey }
  return { kind: 'field', fieldKey }
}

/** What a `data-case-detail` key becomes: never a nearest guess, a key with no home freezes. */
export const tinyMceCaseDetailTarget = (key: string): TinyMceFieldTarget =>
  indexedTarget(key) ??
  catalogTarget(CASE_DETAIL_FIELD_KEYS.get(key) ?? key, key)

/**
 * The service row a specific-service template is bound to, from its
 * URL-encoded `data-properties`: a row id binds both the run's service column
 * and its params, as the editor's own service insert does, since a run that
 * carried only the param would resolve as severed; an index binds through
 * the params alone. Unreadable properties bind nothing.
 */
const serviceBinding = (
  el: Element,
): { caseServiceId?: string; params?: EditorFieldParams } => {
  const raw = el.getAttribute('data-properties')
  if (!raw) return {}
  let properties: unknown
  try {
    properties = JSON.parse(decodeURIComponent(raw))
  } catch {
    return {}
  }
  const { serviceId, serviceIndex } = (properties ?? {}) as {
    serviceId?: unknown
    serviceIndex?: unknown
  }
  if (typeof serviceId === 'string' && serviceId.trim() !== '')
    return { caseServiceId: serviceId, params: { serviceId } }
  if (
    typeof serviceIndex === 'number' &&
    Number.isInteger(serviceIndex) &&
    serviceIndex >= 0
  )
    return { params: { serviceIndex } }
  return {}
}

/**
 * What a `data-template` element becomes, or undefined for an element that
 * is no template. A service or visitation template follows the case's
 * default row, which TinyMCE points it at again on every load, so it
 * converts unbound whatever row its properties name (`default_service` is
 * TinyMCE's older spelling of `service`). `specific_service` is the service
 * composite bound to its stored row, whatever the row's kind.
 */
export const tinyMceTemplateTarget = (
  el: Element,
): TinyMceFieldTarget | undefined => {
  const template = el.getAttribute('data-template')
  if (!template) return undefined
  if (template === 'specific_service')
    return { kind: 'field', fieldKey: 'service', ...serviceBinding(el) }
  if (template === 'service' || template === 'default_service')
    return { kind: 'field', fieldKey: 'service' }
  if (template === 'visitation')
    return { kind: 'field', fieldKey: 'visitation' }
  const target = catalogTarget(
    TEMPLATE_FIELD_KEYS.get(template) ?? template,
    template,
  )
  return target.kind === 'tabular' ? { kind: 'frozen', key: template } : target
}
